import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {z as zod} from "zod";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

export enum OrganizationalUnitTemplateAdditionalColumnType {
    email = "0eeef303-84d6-4163-a323-e0ef4c8a0468",
    indianPhoneNumber = "144f6c72-a1d9-49d2-accb-a828d6a5852e",
    // internationalPhoneNumber = "d6042f6d-a671-484c-8b25-ee08e25e156e",
    number = "61ec7adb-4554-4271-957f-9394ace09539",
    // Retired after the select-options-to-UDO migration: select = "0a3d0906-0462-41bc-99ce-7d74b5d2eed3",
    text = "ef46fdb3-34d5-418a-8382-ff6b2f18649f",
    unknown = "d152fb65-0a97-45be-89d1-8c8c5ff20778",
    userDefinedObject = "b89f873b-24f9-41e3-b0c8-c495972b4414",
    userDefinedObjects = "e69278d3-4c20-49df-8a37-bf93b5f9072f",
    uuid = "0b45d4dd-1cfb-4bfb-8fb4-7ce04d38cbd2",
};

export type OrganizationalUnitTemplateAdditionalColumnConfigurationMap = {
    [OrganizationalUnitTemplateAdditionalColumnType.email]: {required: boolean};
    [OrganizationalUnitTemplateAdditionalColumnType.indianPhoneNumber]: {required: boolean};
    [OrganizationalUnitTemplateAdditionalColumnType.number]: {required: boolean};
    [OrganizationalUnitTemplateAdditionalColumnType.text]: {required: boolean};
    [OrganizationalUnitTemplateAdditionalColumnType.unknown]: {required: boolean};
    [OrganizationalUnitTemplateAdditionalColumnType.userDefinedObject]: {
        required: boolean;
        userDefinedObjectTemplateId: Uuid;
    };
    [OrganizationalUnitTemplateAdditionalColumnType.userDefinedObjects]: {
        required: boolean;
        userDefinedObjectTemplateId: Uuid;
    };
    [OrganizationalUnitTemplateAdditionalColumnType.uuid]: {required: boolean};
};

export type OrganizationalUnitTemplateAdditionalColumnResolver = Record<string, unknown>;

export type OrganizationalUnitTemplateAdditionalColumn = {
    displayName: string;
    id: Uuid;
    resolver?: OrganizationalUnitTemplateAdditionalColumnResolver;
} & {
    [K in OrganizationalUnitTemplateAdditionalColumnType]: {
        configuration: OrganizationalUnitTemplateAdditionalColumnConfigurationMap[K];
        type: K;
    };
}[OrganizationalUnitTemplateAdditionalColumnType];

const organizationalUnitTemplateAdditionalColumnBaseValidator = zod.object({
    displayName: zod.string(),
    id: uuidZodValidator,
    resolver: zod.record(zod.string(), zod.unknown()).optional(),
});

export const organizationalUnitTemplateAdditionalColumnsZodValidator: zod.ZodType<OrganizationalUnitTemplateAdditionalColumn> = zod.discriminatedUnion("type", [
    organizationalUnitTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(OrganizationalUnitTemplateAdditionalColumnType.uuid),
    }),
    organizationalUnitTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(OrganizationalUnitTemplateAdditionalColumnType.email),
    }),
    organizationalUnitTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(OrganizationalUnitTemplateAdditionalColumnType.indianPhoneNumber),
    }),
    organizationalUnitTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(OrganizationalUnitTemplateAdditionalColumnType.number),
    }),
    organizationalUnitTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(OrganizationalUnitTemplateAdditionalColumnType.text),
    }),
    organizationalUnitTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({required: zod.boolean()}),
        type: zod.literal(OrganizationalUnitTemplateAdditionalColumnType.unknown),
    }),
    organizationalUnitTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            required: zod.boolean(),
            userDefinedObjectTemplateId: uuidZodValidator,
        }),
        type: zod.literal(OrganizationalUnitTemplateAdditionalColumnType.userDefinedObject),
    }),
    organizationalUnitTemplateAdditionalColumnBaseValidator.extend({
        configuration: zod.object({
            required: zod.boolean(),
            userDefinedObjectTemplateId: uuidZodValidator,
        }),
        type: zod.literal(OrganizationalUnitTemplateAdditionalColumnType.userDefinedObjects),
    }),
]);

// function getSchemaPropertyTypeFromOrganizationalUnitTemplateAdditionalColumnType(additionalColumnType: OrganizationalUnitTemplateAdditionalColumnType): SchemaPropertyType {
//     switch (additionalColumnType) {
//         case OrganizationalUnitTemplateAdditionalColumnType.email: {
//             return SchemaPropertyType.string;
//         }
//         case OrganizationalUnitTemplateAdditionalColumnType.indianPhoneNumber: {
//             return SchemaPropertyType.string;
//         }
//         case OrganizationalUnitTemplateAdditionalColumnType.number: {
//             return SchemaPropertyType.number;
//         }
//         case OrganizationalUnitTemplateAdditionalColumnType.select: {
//             return SchemaPropertyType.uuid;
//         }
//         case OrganizationalUnitTemplateAdditionalColumnType.text: {
//             return SchemaPropertyType.string;
//         }
//         // TODO: Revisit this
//         case OrganizationalUnitTemplateAdditionalColumnType.unknown: {
//             return SchemaPropertyType.string;
//         }
//         case OrganizationalUnitTemplateAdditionalColumnType.uuid: {
//             return SchemaPropertyType.uuid;
//         }
//         default: {
//             throw new Error(`Unsupported organizational unit template additional column type: ${String(additionalColumnType)}`);
//         }
//     }
// }

// export function getSchemaPropertiesFromOrganizationalUnitTemplateAdditionalColumns(
//     additionalColumns: Array<OrganizationalUnitTemplateAdditionalColumn>,
//     //
// ): Record<string, SchemaProperty>
//  {
//     const properties: Record<string, SchemaProperty> = {};
//     for (const [key, value] of Object.entries(organizationalUnitsWorkspaceCapabilityDefaultColumns)) {
//         properties[key] = {
//             id: generateUuid(),
//             ...value,
//         };
//     }

//     for (const additionalColumn of additionalColumns) {
//         const columnNameInSnakeCase = toSnakeCase(additionalColumn.displayName);
//         if (properties[columnNameInSnakeCase] !== undefined) {
//             throw new Error(`Duplicate additional column display name: ${additionalColumn.displayName}`);
//         }
//         properties[columnNameInSnakeCase] = {
//             displayName: additionalColumn.displayName,
//             id: additionalColumn.id,
//             internalName: toCamelCase(additionalColumn.displayName),
//             required: additionalColumn.configuration.required,
//             type: getSchemaPropertyTypeFromOrganizationalUnitTemplateAdditionalColumnType(additionalColumn.type),
//         };
//     }
//     return properties;
// }
